import React, { useState, useEffect, useCallback } from 'react';
import {
  FiArrowLeft, FiDollarSign, FiPlus, FiMinus,
  FiXCircle, FiCheckCircle, FiRefreshCw, FiUnlock,
} from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtFecha, fmtMoney, fmtHora } from '../utils/format';
import '../styles/Cajas.css';

const DENOMINACIONES = [
  { label: 'S/ 200', val: 200 },
  { label: 'S/ 100', val: 100 },
  { label: 'S/  50', val: 50 },
  { label: 'S/  20', val: 20 },
  { label: 'S/  10', val: 10 },
  { label: 'S/   5', val: 5 },
  { label: 'S/   2', val: 2 },
  { label: 'S/   1', val: 1 },
  { label: 'S/ 0.50', val: 0.5 },
  { label: 'S/ 0.20', val: 0.20 },
  { label: 'S/ 0.10', val: 0.10 },
];

export default function CajaAperturaPage() {
  const { user }  = useAuth();
  const esAdmin   = !!(user?.rol === 'admin' || user?.perfil_es_admin);

  const [view,       setView]       = useState('lista');   // 'lista' | 'sesion'
  const [cajas,      setCajas]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [selectedCaja, setSelectedCaja] = useState(null);
  const [summary,    setSummary]    = useState(null);

  // Modales
  const [showAbrir,  setShowAbrir]  = useState(false);
  const [showCerrar, setShowCerrar] = useState(false);
  const [showMov,    setShowMov]    = useState(null);   // 'INGRESO' | 'EGRESO' | null

  // Forms
  const [abrirForm,  setAbrirForm]  = useState({ monto_apertura: '', almacen_id: '', observacion_apertura: '' });
  const [cerrarForm, setCerrarForm] = useState({ observacion_cierre: '' });
  const [denoms,     setDenoms]     = useState(Object.fromEntries(DENOMINACIONES.map(d => [d.val, ''])));
  const [movForm,    setMovForm]    = useState({ concepto: '', monto: '' });

  const [saving,     setSaving]     = useState(false);
  const [modalError, setModalError] = useState('');

  // ── Load cajas con estado ─────────────────────────────────────────────

  const loadCajas = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/api/cajas/estado');
      setCajas(r.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCajas(); }, [loadCajas]);

  // ── Load summary for active session ──────────────────────────────────

  const loadSummary = useCallback(async (sesionId) => {
    try {
      const r = await api.get(`/api/cajas/sesiones/${sesionId}/summary`);
      setSummary(r.data.data);
    } catch (e) {
      console.error('Error loading summary', e);
    }
  }, []);

  useEffect(() => {
    if (view === 'sesion' && selectedCaja?.sesion_activa?.id) {
      loadSummary(selectedCaja.sesion_activa.id);
    }
  }, [view, selectedCaja, loadSummary]);

  // ── Abrir caja ────────────────────────────────────────────────────────

  const handleOpenAbrir = (caja) => {
    setSelectedCaja(caja);
    setAbrirForm({
      monto_apertura: '',
      almacen_id: caja.almacenes?.find(a => a.es_default)?.almacen_id || caja.almacenes?.[0]?.almacen_id || '',
      observacion_apertura: '',
    });
    setModalError('');
    setShowAbrir(true);
  };

  const handleAbrir = async () => {
    const monto = parseFloat(abrirForm.monto_apertura);
    if (isNaN(monto) || monto < 0) { setModalError('Ingresa un monto de apertura válido (0 o más).'); return; }
    setSaving(true); setModalError('');
    try {
      await api.post(`/api/cajas/${selectedCaja.id}/abrir`, {
        monto_apertura: monto,
        almacen_id: abrirForm.almacen_id || null,
        observacion_apertura: abrirForm.observacion_apertura,
      });
      setShowAbrir(false);
      const r = await api.get('/api/cajas/estado');
      const updated = (r.data.data || []).find(c => c.id === selectedCaja.id);
      setCajas(r.data.data || []);
      if (updated) {
        setSelectedCaja(updated);
        setView('sesion');
      }
    } catch (e) {
      setModalError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Ver sesión ────────────────────────────────────────────────────────

  const handleVerSesion = async (caja) => {
    setSelectedCaja(caja);
    setSummary(null);
    setView('sesion');
  };

  // ── Cerrar caja ───────────────────────────────────────────────────────

  const montoCierre = Object.entries(denoms).reduce(
    (sum, [val, qty]) => sum + (parseFloat(val) * (parseInt(qty) || 0)), 0
  );

  const handleCerrar = async () => {
    setSaving(true); setModalError('');
    try {
      await api.post(`/api/cajas/sesiones/${selectedCaja.sesion_activa.id}/cerrar`, {
        monto_cierre: montoCierre,
        observacion_cierre: cerrarForm.observacion_cierre,
      });
      setShowCerrar(false);
      setDenoms(Object.fromEntries(DENOMINACIONES.map(d => [d.val, ''])));
      setCerrarForm({ observacion_cierre: '' });
      setView('lista');
      loadCajas();
    } catch (e) {
      setModalError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Movimientos ───────────────────────────────────────────────────────

  const handleAddMov = async () => {
    const monto = parseFloat(movForm.monto);
    if (!monto || monto <= 0) { setModalError('El monto debe ser mayor a 0.'); return; }
    setSaving(true); setModalError('');
    try {
      await api.post(`/api/cajas/sesiones/${selectedCaja.sesion_activa.id}/movimientos`, {
        tipo:     showMov,
        concepto: movForm.concepto,
        monto,
      });
      setShowMov(null);
      setMovForm({ concepto: '', monto: '' });
      loadSummary(selectedCaja.sesion_activa.id);
    } catch (e) {
      setModalError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Render: Lista de cajas ────────────────────────────────────────────

  if (view === 'lista') return (
    <div className="cj-page">
      <div className="cj-toolbar" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
          Apertura / Cierre de Cajas
        </span>
        <button
          className="cj-btn-icon cj-btn-edit"
          style={{ width: 'auto', padding: '5px 12px', fontSize: 12, display: 'flex', gap: 5 }}
          onClick={loadCajas}
          title="Actualizar"
        >
          <FiRefreshCw size={12} /> Actualizar
        </button>
      </div>

      {loading ? (
        <div className="cj-loading">Cargando...</div>
      ) : error ? (
        <div className="cj-error">{error}</div>
      ) : cajas.length === 0 ? (
        <div className="cj-empty">
          No hay cajas activas. Crea una en <strong>Gestión Cajas</strong>.
        </div>
      ) : (
        <div className="cj-cards-grid">
          {(() => {
            const hayAlgunaAbierta = cajas.some(c => !!c.sesion_activa);
            return cajas.map(caja => {
              const sesion        = caja.sesion_activa;
              const cerradaHoy    = caja.cerrada_hoy;
              const isOpen        = !!sesion;
              const isCerradaHoy  = !isOpen && !!cerradaHoy;
              const otraAbierta   = !isOpen && hayAlgunaAbierta;

              return (
                <div
                  key={caja.id}
                  className={`cj-card ${isOpen ? 'cj-card-open' : isCerradaHoy ? 'cj-card-cerrada-hoy' : ''}`}
                >
                  <div className="cj-card-header">
                    <div>
                      <div className="cj-card-name">{caja.nombre}</div>
                      {caja.descripcion && <div className="cj-card-desc">{caja.descripcion}</div>}
                    </div>
                    <span className={`cj-badge ${isOpen ? 'cj-badge-open' : isCerradaHoy ? 'cj-badge-closed' : 'cj-badge-nosesion'}`}>
                      {isOpen ? 'ABIERTA' : isCerradaHoy ? 'CERRADA HOY' : 'SIN SESIÓN'}
                    </span>
                  </div>

                  <div className="cj-card-almacenes">
                    {(caja.almacenes || []).map(a => (
                      <span
                        key={a.almacen_id}
                        className={`cj-chip ${a.es_default ? 'cj-chip-default' : ''}`}
                      >
                        {a.almacen_nombre}{a.es_default ? ' ★' : ''}
                      </span>
                    ))}
                  </div>

                  {isOpen && (
                    <div className="cj-card-info">
                      <div>Abierta desde: <span>{fmtHora(sesion.abierto_en)}</span></div>
                      <div>Apertura: <span>S/ {fmtMoney(sesion.monto_apertura)}</span></div>
                    </div>
                  )}
                  {isCerradaHoy && (
                    <div className="cj-card-info">
                      <div>Cerrada a las: <span>{fmtHora(cerradaHoy.cerrado_en)}</span></div>
                      {esAdmin
                        ? <div style={{ fontSize: 11, color: '#1d4ed8', marginTop: 4 }}>Solo administradores pueden reabrir.</div>
                        : <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Solo un administrador puede reabrir esta caja.</div>
                      }
                    </div>
                  )}
                  {otraAbierta && !isOpen && !isCerradaHoy && (
                    <div className="cj-card-info">
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>Cierra la caja abierta antes de abrir esta.</div>
                    </div>
                  )}

                  <div className="cj-card-actions">
                    {isOpen ? (
                      <button className="cj-btn-ver-sesion" onClick={() => handleVerSesion(caja)}>
                        <FiDollarSign size={13} /> Ver Sesión
                      </button>
                    ) : isCerradaHoy ? (
                      esAdmin ? (
                        <button className="cj-btn-abrir" onClick={() => handleOpenAbrir(caja)} style={{ background: '#1d4ed8' }}>
                          <FiUnlock size={13} /> Reabrir Caja
                        </button>
                      ) : (
                        <button className="cj-btn-abrir" disabled style={{ opacity: 0.45, cursor: 'not-allowed' }}>
                          <FiXCircle size={13} /> Cerrada por hoy
                        </button>
                      )
                    ) : otraAbierta ? (
                      <button className="cj-btn-abrir" disabled style={{ opacity: 0.45, cursor: 'not-allowed' }} title="Cierra la otra caja primero">
                        <FiXCircle size={13} /> Otra caja abierta
                      </button>
                    ) : (
                      <button className="cj-btn-abrir" onClick={() => handleOpenAbrir(caja)}>
                        <FiCheckCircle size={13} /> Abrir Caja
                      </button>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      <div className="cj-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {/* Modal Abrir */}
      {showAbrir && selectedCaja && (
        <div className="cj-overlay">
          <div className="cj-modal" onClick={e => e.stopPropagation()}>
            <div className="cj-modal-header">
              <span>{selectedCaja.cerrada_hoy ? 'Reabrir Caja' : 'Abrir Caja'} — {selectedCaja.nombre}</span>
              <button className="cj-modal-close" onClick={() => setShowAbrir(false)}>×</button>
            </div>
            <div className="cj-modal-body">
              <div className="cj-field">
                <label className="cj-label">Monto de Apertura (S/) *</label>
                <input
                  className="cj-input"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={abrirForm.monto_apertura}
                  onChange={e => { setModalError(''); setAbrirForm(p => ({ ...p, monto_apertura: e.target.value })); }}
                  autoFocus
                />
              </div>
              {selectedCaja.almacenes?.length > 1 && (
                <div className="cj-field">
                  <label className="cj-label">Almacén activo para esta sesión</label>
                  <select
                    className="cj-select"
                    value={abrirForm.almacen_id}
                    onChange={e => setAbrirForm(p => ({ ...p, almacen_id: e.target.value }))}
                  >
                    {selectedCaja.almacenes.map(a => (
                      <option key={a.almacen_id} value={a.almacen_id}>
                        {a.almacen_nombre}{a.es_default ? ' (por defecto)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="cj-field">
                <label className="cj-label">Observación</label>
                <textarea
                  className="cj-textarea"
                  placeholder="Observaciones de apertura (opcional)"
                  value={abrirForm.observacion_apertura}
                  onChange={e => setAbrirForm(p => ({ ...p, observacion_apertura: e.target.value }))}
                />
              </div>
            </div>
            {modalError && <div className="cj-modal-error">{modalError}</div>}
            <div className="cj-modal-footer">
              <button className="cj-btn-save" onClick={handleAbrir} disabled={saving}>
                {saving ? 'Abriendo...' : 'Confirmar Apertura'}
              </button>
              <button className="cj-btn-cancel" onClick={() => setShowAbrir(false)}>× Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Render: Detalle de sesión ─────────────────────────────────────────

  const sesion  = selectedCaja?.sesion_activa;
  const isOpen  = sesion?.estado === 'ABIERTA';
  const movs    = summary?.movimientos || [];

  return (
    <div className="cj-sesion-page">
      {/* Header */}
      <div className="cj-sesion-header">
        <button className="cj-sesion-back" onClick={() => { setView('lista'); setSummary(null); }}>
          <FiArrowLeft size={13} /> Cajas
        </button>
        <div className="cj-sesion-title">{selectedCaja?.nombre}</div>
        <div className="cj-sesion-meta">
          <span>Fecha: {fmtFecha(sesion?.fecha)}</span>
          <span>Apertura: {fmtHora(sesion?.abierto_en)}</span>
          {sesion?.almacen_nombre && <span>Almacén: {sesion.almacen_nombre}</span>}
          <span
            className={`cj-badge ${isOpen ? 'cj-badge-open' : 'cj-badge-closed'}`}
          >
            {isOpen ? 'ABIERTA' : 'CERRADA'}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="cj-sesion-body">
        {/* Panel resumen */}
        <div>
          <div className="cj-summary-panel">
            <div className="cj-summary-title">Resumen de Caja</div>

            <div className="cj-summary-row">
              <span className="cj-summary-label">Monto Apertura</span>
              <span className="cj-summary-value">S/ {fmtMoney(summary?.monto_apertura)}</span>
            </div>
            <div className="cj-summary-row">
              <span className="cj-summary-label">Ventas Efectivo</span>
              <span className="cj-summary-value">S/ {fmtMoney(summary?.ventas_efectivo)}</span>
            </div>
            <div className="cj-summary-row">
              <span className="cj-summary-label">Ingresos Efectivo</span>
              <span className="cj-summary-value cj-summary-value-pos">+ S/ {fmtMoney(summary?.ingresos)}</span>
            </div>
            {(summary?.ingresos_digitales || 0) > 0 && (
              <div className="cj-summary-row">
                <span className="cj-summary-label">Ingresos Digitales</span>
                <span className="cj-summary-value" style={{ color: '#0369a1' }}>S/ {fmtMoney(summary?.ingresos_digitales)}</span>
              </div>
            )}
            <div className="cj-summary-row">
              <span className="cj-summary-label">Egresos</span>
              <span className="cj-summary-value cj-summary-value-neg">- S/ {fmtMoney(summary?.egresos)}</span>
            </div>

            <div className="cj-summary-divider" />

            <div className="cj-summary-row cj-summary-total">
              <span className="cj-summary-label">TOTAL EFECTIVO</span>
              <span className="cj-summary-value">S/ {fmtMoney(summary?.total_efectivo)}</span>
            </div>

            {!isOpen && sesion?.monto_cierre != null && (
              <>
                <div className="cj-summary-row">
                  <span className="cj-summary-label">Monto Cierre</span>
                  <span className="cj-summary-value">S/ {fmtMoney(sesion.monto_cierre)}</span>
                </div>
                <div className="cj-summary-row">
                  <span className="cj-summary-label">Diferencia</span>
                  <span className={`cj-summary-value ${
                    parseFloat(sesion.diferencia) > 0 ? 'cj-summary-value-pos' :
                    parseFloat(sesion.diferencia) < 0 ? 'cj-summary-value-neg' : ''
                  }`}>
                    {parseFloat(sesion.diferencia) >= 0 ? '+' : ''}S/ {fmtMoney(sesion.diferencia)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Resumen de ventas de la sesión */}
          {summary?.ventas && (
            <div className="cj-ventas-panel">
              <div className="cj-summary-title" style={{ marginBottom: 8 }}>Ventas de la Sesión</div>

              <div className="cj-ventas-kpis">
                <div className="cj-ventas-kpi">
                  <div className="cj-ventas-kpi-label">Completadas</div>
                  <div className="cj-ventas-kpi-val">
                    {summary.ventas.nro_ventas - summary.ventas.nro_anuladas}
                  </div>
                </div>
                {summary.ventas.nro_anuladas > 0 && (
                  <div className="cj-ventas-kpi">
                    <div className="cj-ventas-kpi-label">Anuladas</div>
                    <div className="cj-ventas-kpi-val cj-ventas-kpi-null">
                      {summary.ventas.nro_anuladas}
                    </div>
                  </div>
                )}
                <div className="cj-ventas-kpi">
                  <div className="cj-ventas-kpi-label">Total</div>
                  <div className="cj-ventas-kpi-val cj-ventas-kpi-total">
                    S/ {fmtMoney(summary.ventas.total_ventas)}
                  </div>
                </div>
              </div>

              <div className="cj-summary-row" style={{ marginTop: 8 }}>
                <span className="cj-summary-label">Contado</span>
                <span className="cj-summary-value cj-summary-value-pos">
                  S/ {fmtMoney(summary.ventas.total_contado)}
                </span>
              </div>
              <div className="cj-summary-row">
                <span className="cj-summary-label">Crédito</span>
                <span className="cj-summary-value" style={{ color: '#92400e' }}>
                  S/ {fmtMoney(summary.ventas.total_credito)}
                </span>
              </div>

              {(summary.ventas_por_forma_pago || []).length > 0 && (
                <>
                  <div className="cj-summary-divider" />
                  <div className="cj-summary-title" style={{ marginBottom: 6, fontSize: 12 }}>
                    Contado por Forma de Pago
                  </div>
                  {summary.ventas_por_forma_pago.map((fp) => (
                    <div className="cj-summary-row" key={fp.abreviatura}>
                      <span className="cj-summary-label">
                        {fp.forma_pago} <span style={{ color: '#94a3b8' }}>({fp.nro})</span>
                      </span>
                      <span className={`cj-summary-value ${fp.abreviatura === 'EFECTIVO' ? 'cj-summary-value-pos' : ''}`}>
                        S/ {fmtMoney(fp.monto)}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {isOpen && (
            <button
              className="cj-btn-cerrar"
              style={{ width: '100%', marginTop: 12 }}
              onClick={() => { setModalError(''); setShowCerrar(true); }}
            >
              <FiXCircle size={14} /> Cerrar Caja
            </button>
          )}
        </div>

        {/* Panel movimientos */}
        <div className="cj-mov-panel">
          <div className="cj-mov-header">
            <span className="cj-mov-title">Movimientos de Caja</span>
            {isOpen && (
              <div className="cj-mov-btns">
                <button className="cj-btn-ingreso" onClick={() => { setMovForm({ concepto: '', monto: '' }); setModalError(''); setShowMov('INGRESO'); }}>
                  <FiPlus size={11} /> Ingreso
                </button>
                <button className="cj-btn-egreso" onClick={() => { setMovForm({ concepto: '', monto: '' }); setModalError(''); setShowMov('EGRESO'); }}>
                  <FiMinus size={11} /> Egreso
                </button>
              </div>
            )}
          </div>

          <div className="cj-mov-list">
            {movs.length === 0 ? (
              <div className="cj-mov-empty">Sin movimientos registrados</div>
            ) : movs.map(m => (
              <div key={m.id} className="cj-mov-item">
                <span className={`cj-mov-type ${m.tipo === 'INGRESO' ? 'cj-mov-type-ingreso' : 'cj-mov-type-egreso'}`}>
                  {m.tipo === 'INGRESO' ? '+' : '-'}
                </span>
                <span className="cj-mov-concepto">{m.concepto || (m.tipo === 'INGRESO' ? 'Ingreso' : 'Egreso')}</span>
                <span className="cj-mov-hora">{fmtHora(m.creado_en)}</span>
                <span className={`cj-mov-monto ${m.tipo === 'INGRESO' ? 'cj-mov-monto-ingreso' : 'cj-mov-monto-egreso'}`}>
                  {m.tipo === 'INGRESO' ? '+' : '-'} S/ {fmtMoney(m.monto)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal: Agregar movimiento */}
      {showMov && (
        <div className="cj-overlay">
          <div className="cj-modal" onClick={e => e.stopPropagation()} style={{ width: 400 }}>
            <div className="cj-modal-header">
              <span style={{ color: showMov === 'INGRESO' ? '#86efac' : '#fca5a5' }}>
                {showMov === 'INGRESO' ? '+ Agregar Ingreso' : '− Agregar Egreso'}
              </span>
              <button className="cj-modal-close" onClick={() => setShowMov(null)}>×</button>
            </div>
            <div className="cj-modal-body">
              <div className="cj-field">
                <label className="cj-label">Concepto</label>
                <input
                  className="cj-input"
                  placeholder={showMov === 'INGRESO' ? 'Ej: Préstamo, cobro extra...' : 'Ej: Pago proveedor, gastos...'}
                  value={movForm.concepto}
                  onChange={e => { setModalError(''); setMovForm(p => ({ ...p, concepto: e.target.value })); }}
                  autoFocus
                />
              </div>
              <div className="cj-field">
                <label className="cj-label">Monto (S/) *</label>
                <input
                  className="cj-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={movForm.monto}
                  onChange={e => { setModalError(''); setMovForm(p => ({ ...p, monto: e.target.value })); }}
                />
              </div>
            </div>
            {modalError && <div className="cj-modal-error">{modalError}</div>}
            <div className="cj-modal-footer">
              <button className="cj-btn-save" onClick={handleAddMov} disabled={saving}>
                {saving ? 'Guardando...' : 'Confirmar'}
              </button>
              <button className="cj-btn-cancel" onClick={() => setShowMov(null)}>× Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Cerrar caja */}
      {showCerrar && (
        <div className="cj-overlay">
          <div className="cj-modal cj-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="cj-modal-header">
              <span>Cierre de Caja — {selectedCaja?.nombre}</span>
              <button className="cj-modal-close" onClick={() => setShowCerrar(false)}>×</button>
            </div>
            <div className="cj-modal-body">
              <p style={{ fontSize: 12.5, color: '#64748b', margin: 0 }}>
                Ingresa la cantidad de cada denominación contada físicamente.
              </p>

              <div className="cj-denom-grid">
                {DENOMINACIONES.map(d => {
                  const qty    = parseInt(denoms[d.val]) || 0;
                  const subTot = qty * d.val;
                  return (
                    <div key={d.val} className="cj-denom-row">
                      <span className="cj-denom-label">{d.label}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>×</span>
                      <input
                        className="cj-denom-input"
                        type="number"
                        min="0"
                        placeholder="0"
                        value={denoms[d.val]}
                        onChange={e => setDenoms(p => ({ ...p, [d.val]: e.target.value }))}
                      />
                      <span className="cj-denom-subtotal">
                        {subTot > 0 ? `S/ ${fmtMoney(subTot)}` : ''}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="cj-denom-total-row">
                <span className="cj-denom-total-label">TOTAL CONTADO</span>
                <span className="cj-denom-total-value">S/ {fmtMoney(montoCierre)}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px', fontSize: 12.5 }}>
                <span style={{ color: '#64748b' }}>Total Efectivo en Sistema:</span>
                <span style={{ fontWeight: 700 }}>S/ {fmtMoney(summary?.total_efectivo)}</span>
              </div>

              <div style={{
                display: 'flex', justifyContent: 'space-between', padding: '8px 12px',
                borderRadius: 6, fontSize: 13, fontWeight: 700,
                background: Math.abs(montoCierre - (summary?.total_efectivo || 0)) < 0.01
                  ? '#dcfce7' : '#fee2e2',
                color: Math.abs(montoCierre - (summary?.total_efectivo || 0)) < 0.01
                  ? '#166534' : '#991b1b',
              }}>
                <span>Diferencia:</span>
                <span>
                  {montoCierre - (summary?.total_efectivo || 0) >= 0 ? '+' : ''}
                  S/ {fmtMoney(montoCierre - (summary?.total_efectivo || 0))}
                </span>
              </div>

              <div className="cj-field">
                <label className="cj-label">Observación de Cierre</label>
                <textarea
                  className="cj-textarea"
                  placeholder="Observaciones opcionales..."
                  value={cerrarForm.observacion_cierre}
                  onChange={e => setCerrarForm(p => ({ ...p, observacion_cierre: e.target.value }))}
                />
              </div>
            </div>
            {modalError && <div className="cj-modal-error">{modalError}</div>}
            <div className="cj-modal-footer">
              <button className="cj-btn-save" style={{ background: '#dc2626' }} onClick={handleCerrar} disabled={saving}>
                {saving ? 'Cerrando...' : 'Confirmar Cierre'}
              </button>
              <button className="cj-btn-cancel" onClick={() => setShowCerrar(false)}>× Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
